import dayjs from 'dayjs'

import type { PerformanceChartData } from '@/lib/types/performance.types'

export type ChartGranularity = 'day' | 'week' | 'month' | 'quarter' | 'year'

const GRANULARITIES: ChartGranularity[] = ['day', 'week', 'month', 'quarter', 'year']

const MIN_POINT_SPACING = 32
const MIN_POINTS = 2
const MAX_WEEKS = 12

export const maxPointsForWidth = (width: number): number =>
  Math.max(MIN_POINTS, Math.floor(width / MIN_POINT_SPACING))

const bucketKey = (date: string, granularity: ChartGranularity): string => {
  const day = dayjs(date)
  switch (granularity) {
    case 'day':
      return day.format('YYYY-MM-DD')
    case 'week': {
      const daysSinceMonday = (day.day() + 6) % 7
      return day.subtract(daysSinceMonday, 'day').format('YYYY-MM-DD')
    }
    case 'month':
      return day.startOf('month').format('YYYY-MM-DD')
    case 'quarter':
      return day
        .month(Math.floor(day.month() / 3) * 3)
        .startOf('month')
        .format('YYYY-MM-DD')
    case 'year':
      return day.startOf('year').format('YYYY-MM-DD')
  }
}

const distinctBucketCount = (labels: string[], granularity: ChartGranularity): number => {
  const keys = new Set<string>()
  for (const label of labels) {
    keys.add(bucketKey(label, granularity))
  }
  return keys.size
}

export const chooseGranularity = (labels: string[], maxPoints: number): ChartGranularity => {
  for (const granularity of GRANULARITIES) {
    const count = distinctBucketCount(labels, granularity)
    if (count > maxPoints) {
      continue
    }
    if (granularity === 'week' && count > MAX_WEEKS) {
      continue
    }
    return granularity
  }
  return 'year'
}

export type AggregatedSeries = {
  labels: string[]
  values: number[]
}

export type AggregatedChartData = {
  currentLabels: string[]
  previousLabels: string[]
  current: number[]
  previous: number[]
  granularity: ChartGranularity
}

const bucketSeries = (
  labels: string[],
  values: number[],
  granularity: ChartGranularity,
): AggregatedSeries => {
  if (granularity === 'day' || labels.length === 0) {
    return { labels, values }
  }
  const order: string[] = []
  const byBucket = new Map<string, number>()
  labels.forEach((label, index) => {
    const key = bucketKey(label, granularity)
    if (!byBucket.has(key)) {
      order.push(key)
      byBucket.set(key, 0)
    }
    byBucket.set(key, (byBucket.get(key) ?? 0) + (values[index] ?? 0))
  })
  return { labels: order, values: order.map((key) => byBucket.get(key) ?? 0) }
}

/**
 * Aggregates the current and previous series independently by their own dates, so the two are
 * aligned by ordinal position (bucket index) rather than by calendar date. Granularity is chosen
 * from the current series; a shorter previous series simply leaves trailing indices unset.
 */
export const aggregatePerformanceChart = (
  data: PerformanceChartData,
  maxPoints: number,
): AggregatedChartData => {
  const granularity = chooseGranularity(data.labels, maxPoints)
  const current = bucketSeries(data.labels, data.current, granularity)
  const previous = bucketSeries(data.previousLabels, data.previous, granularity)
  return {
    currentLabels: current.labels,
    previousLabels: previous.labels,
    current: current.values,
    previous: previous.values,
    granularity,
  }
}
